import { supabase } from './supabase';
import { useCreditsStore } from '../store/creditsStore';
import { accountValidationService } from './accountValidationService';
import { referrerLookupService } from './referrerLookupService';

const SIGNUP_CREDIT_REWARD = 25;
const SUBSCRIPTION_CREDIT_REWARD = 75;

export type ConnectionReason = 'friend' | 'colleague' | 'family' | 'classmate' | 'mentor' | 'other';

export interface ReferralApplicationData {
  referrerContact: string;
  connectionReason: ConnectionReason;
  connectionReasonOther?: string;
}

export const referralService = {
  async generateReferralCode(userId: string): Promise<string> {
    const { data, error } = await supabase.rpc('generate_referral_code');

    if (error) throw error;

    const code = data as string;

    await supabase.from('referral_codes').insert({
      user_id: userId,
      code: code,
    });

    await supabase.from('profiles').update({
      referral_code: code,
    }).eq('id', userId);

    return code;
  },

  async getReferralCode(userId: string): Promise<string | null> {
    const { data: profile } = await supabase
      .from('profiles')
      .select('referral_code')
      .eq('id', userId)
      .maybeSingle();

    if (profile?.referral_code) {
      return profile.referral_code;
    }

    return await this.generateReferralCode(userId);
  },

  async validateReferralCode(code: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('referral_codes')
      .select('id')
      .eq('code', code.toUpperCase())
      .maybeSingle();

    return !error && !!data;
  },

  async applyReferralCode(
    refereeId: string,
    referralCode: string,
    applicationData?: ReferralApplicationData
  ): Promise<boolean> {
    try {
      const upperCode = referralCode.toUpperCase();

      const { data: codeData } = await supabase
        .from('referral_codes')
        .select('user_id')
        .eq('code', upperCode)
        .maybeSingle();

      if (!codeData || codeData.user_id === refereeId) {
        return false;
      }

      await supabase.from('profiles').update({
        referred_by_code: upperCode,
      }).eq('id', refereeId);

      const referrerStatus = await accountValidationService.checkAccountCompletion(
        codeData.user_id
      );
      const refereeStatus = await accountValidationService.checkAccountCompletion(refereeId);

      const bothAccountsVerified = referrerStatus.isComplete && refereeStatus.isComplete;

      const referralData: any = {
        referrer_id: codeData.user_id,
        referee_id: refereeId,
        referral_code: upperCode,
        status: 'signup_completed',
        signup_completed_at: new Date().toISOString(),
        referrer_account_complete: referrerStatus.isComplete,
        referee_account_complete: refereeStatus.isComplete,
        both_accounts_verified: bothAccountsVerified,
        verification_check_date: new Date().toISOString(),
      };

      if (applicationData) {
        referralData.referrer_contact = applicationData.referrerContact;
        referralData.connection_reason = applicationData.connectionReason;
        if (applicationData.connectionReasonOther) {
          referralData.connection_reason_other = applicationData.connectionReasonOther;
        }
      }

      const { error: referralError } = await supabase.from('referrals').insert(referralData);

      if (referralError) throw referralError;

      // Increment uses_count using RPC function
      await supabase.rpc('increment_referral_code_uses', { code_param: upperCode });

      if (bothAccountsVerified) {
        await this.awardSignupCredits(codeData.user_id, refereeId, upperCode);
      }

      return true;
    } catch (error) {
      console.error('Error applying referral code:', error);
      return false;
    }
  },

  async applyReferralByContact(
    refereeId: string,
    referrerContact: string,
    connectionReason: ConnectionReason,
    connectionReasonOther?: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const referrerInfo = await referrerLookupService.findReferrerByContact(referrerContact);

      if (!referrerInfo) {
        return {
          success: false,
          error: 'Referrer not found. Please check the email, phone number, or code.',
        };
      }

      if (referrerInfo.user_id === refereeId) {
        return {
          success: false,
          error: 'You cannot refer yourself.',
        };
      }

      if (!referrerInfo.account_complete) {
        return {
          success: false,
          error: 'The referrer account is not complete. Please ask them to complete their profile first.',
        };
      }

      const applicationData: ReferralApplicationData = {
        referrerContact,
        connectionReason,
        connectionReasonOther,
      };

      const success = await this.applyReferralCode(
        refereeId,
        referrerInfo.referral_code,
        applicationData
      );

      if (!success) {
        return {
          success: false,
          error: 'Failed to apply referral. You may have already been referred.',
        };
      }

      return { success: true };
    } catch (error) {
      console.error('Error applying referral by contact:', error);
      return {
        success: false,
        error: 'An unexpected error occurred. Please try again.',
      };
    }
  },

  async awardSignupCredits(referrerId: string, refereeId: string, code: string): Promise<void> {
    try {
      const { data: referral } = await supabase
        .from('referrals')
        .select('both_accounts_verified, signup_credits_awarded')
        .eq('referee_id', refereeId)
        .maybeSingle();

      if (!referral || !referral.both_accounts_verified || referral.signup_credits_awarded) {
        return;
      }

      const { awardCredits } = useCreditsStore.getState();

      await awardCredits(
        referrerId,
        SIGNUP_CREDIT_REWARD,
        'referral_signup',
        `Referral bonus: New user signed up with your code ${code}`
      );

      await supabase
        .from('referrals')
        .update({ signup_credits_awarded: true })
        .eq('referee_id', refereeId);
    } catch (error) {
      console.error('Error awarding signup credits:', error);
    }
  },

  async checkAndAwardPendingCredits(referralId: string): Promise<void> {
    try {
      const { data: referral } = await supabase
        .from('referrals')
        .select('*')
        .eq('id', referralId)
        .maybeSingle();

      if (!referral) return;

      const validated = await accountValidationService.validateReferralAccounts(referralId);

      if (validated && !referral.signup_credits_awarded) {
        await this.awardSignupCredits(
          referral.referrer_id,
          referral.referee_id,
          referral.referral_code
        );
      }
    } catch (error) {
      console.error('Error checking and awarding pending credits:', error);
    }
  },

  async awardSubscriptionCredits(refereeId: string): Promise<void> {
    try {
      const { data: referral } = await supabase
        .from('referrals')
        .select('*')
        .eq('referee_id', refereeId)
        .eq('subscription_credits_awarded', false)
        .maybeSingle();

      if (!referral || !referral.both_accounts_verified) return;

      const { awardCredits } = useCreditsStore.getState();

      await awardCredits(
        referral.referrer_id,
        SUBSCRIPTION_CREDIT_REWARD,
        'referral_subscription',
        `Referral bonus: User subscribed with your code ${referral.referral_code}`
      );

      await supabase
        .from('referrals')
        .update({
          status: 'subscription_completed',
          subscription_credits_awarded: true,
          subscription_completed_at: new Date().toISOString(),
        })
        .eq('id', referral.id);
    } catch (error) {
      console.error('Error awarding subscription credits:', error);
    }
  },

  async getUserReferrals(userId: string) {
    const { data, error } = await supabase
      .from('referrals')
      .select(`
        *,
        referee:profiles!referrals_referee_id_fkey(full_name, email)
      `)
      .eq('referrer_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return data || [];
  },

  async getReferralStats(userId: string) {
    const { data: referrals } = await supabase
      .from('referrals')
      .select('*')
      .eq('referrer_id', userId);

    const totalReferrals = referrals?.length || 0;
    const verifiedReferrals = referrals?.filter(r => r.both_accounts_verified).length || 0;
    const pendingVerification = referrals?.filter(r => !r.both_accounts_verified).length || 0;
    const signupReferrals = referrals?.filter(r => r.signup_credits_awarded).length || 0;
    const subscriptionReferrals = referrals?.filter(r => r.subscription_credits_awarded).length || 0;
    const totalCreditsEarned = (signupReferrals * SIGNUP_CREDIT_REWARD) + (subscriptionReferrals * SUBSCRIPTION_CREDIT_REWARD);
    const pendingCredits = (verifiedReferrals - signupReferrals) * SIGNUP_CREDIT_REWARD;

    return {
      totalReferrals,
      verifiedReferrals,
      pendingVerification,
      signupReferrals,
      subscriptionReferrals,
      totalCreditsEarned,
      pendingCredits,
      pendingReferrals: totalReferrals - subscriptionReferrals,
    };
  },
};
